import { isPortfolioDocUploadMockEnabled } from 'shared/config/appConfig';
import { toApiHost } from 'shared/config/firebase';
import FirebaseTokenService from 'shared/services/FirebaseTokenService';
import JwtAuthService from 'shared/services/JwtAuthService';
import RestService from 'shared/services/RestService';
import okFixture from './__fixtures__/portfolioDocumentImport/ok.json';
import needsPagesFixture from './__fixtures__/portfolioDocumentImport/needs_pages.json';
import errorFixture from './__fixtures__/portfolioDocumentImport/error.json';

// Client for WS /pro/api/v2/fineco/portfolios/import/document/ws (the POST's
// contract v2 body, docs/plans/2026-10-15-doc-upload-poc-contract.md, plus live
// progress; protocol in api routers/pro/fineco.py). Large documents take minutes
// (a 723-row sheet ~4–5 min), so the advisor sees each step in Italian. The
// document goes to the api only (never to ACE); the confirmed rows reach the
// agent through the existing manual-portfolio action.

const IMPORT_DOCUMENT_WS_PATH = '/pro/api/v2/fineco/portfolios/import/document/ws';
const IMPORT_CONFIG_PATH = '/pro/api/v2/fineco/portfolios/import/document/config';
// The server's frame limit is 16 MiB; 1 MiB chunks stay far below it.
const CHUNK_BYTES = 1024 * 1024;
const MOCK_STEP_MS = 400;

export type ImportStage = 'reading' | 'isin' | 'figures' | 'checks';

/** One server progress event: `message` is the Italian text to show as is. */
export type ImportProgress = {
  stage: ImportStage;
  message: string;
  done: number | null;
  total: number | null;
  elapsedS: number;
};

export type ImportOptions = {
  onProgress?: (progress: ImportProgress) => void;
  /** Aborting closes the socket, which cancels the import on the server. */
  signal?: AbortSignal;
};

export type HoldingStatus = 'ok' | 'check_figures' | 'isin_repaired' | 'out_of_catalog';

export type HoldingIssue =
  | 'missing_value'
  | 'missing_weight'
  | 'missing_quantity'
  | 'arithmetic_mismatch'
  | 'summed_lines'
  | 'isin_ocr_repaired'
  | 'not_in_catalog'
  | 'mixed_currency_no_weight'
  | 'missing_currency';

export type ImportWarning = 'multiple_portfolios' | 'over_page_cap' | 'low_image_quality' | 'no_isin_found';

export type ImportHolding = {
  row_id: string;
  isin: string;
  description: string | null;
  currency: string | null;
  quantity: number | null;
  value: number | null;
  /** Percent: 12.35 = 12.35 %. */
  weight: number | null;
  in_catalog: boolean;
  negotiability: 'consultable' | 'maintainable' | 'non_consultable' | 'unknown' | null;
  status: HoldingStatus;
  issues: HoldingIssue[];
};

export type PageOverview = { page: number; isin_count: number; has_table: boolean };

export type DocumentImportResult = {
  status: 'ok' | 'needs_pages';
  document: { kind: 'pdf' | 'image' | 'xlsx' | 'csv'; page_count: number; pages_used: number[] };
  page_overview: PageOverview[] | null;
  holdings: ImportHolding[];
  totals: { value_sum: number | null; weight_sum: number | null; printed_total: number | null } | null;
  warnings: ImportWarning[];
};

let enabledRequest: Promise<boolean> | null = null;

/**
 * Whether the api has the upload on (its env FINECO_DOC_UPLOAD), asked once per
 * session; a failed request counts as off and is asked again next time. Mock mode
 * (local only) is on without the api.
 */
export function fetchDocUploadEnabled(): Promise<boolean> {
  if (isPortfolioDocUploadMockEnabled()) return Promise.resolve(true);
  if (!enabledRequest) {
    // Local dev only: an absolute URL makes axios skip BACKEND_API_BASE_URL (the upload api runs apart from the login api).
    const base = window.__APP_CONFIG__?.PORTFOLIO_DOC_UPLOAD_API_BASE_URL ?? '';
    enabledRequest = RestService.get<{ enabled?: boolean }>(`${base}${IMPORT_CONFIG_PATH}`, undefined, {
      suppressGlobalErrorToast: true,
    })
      .then((config) => config?.enabled === true)
      .catch(() => {
        enabledRequest = null;
        return false;
      });
  }
  return enabledRequest;
}

/** Tests only: forget the cached answer. */
export const resetDocUploadEnabled = () => {
  enabledRequest = null;
};

/**
 * Upload one document (optionally restricted to `pages`, e.g. "1-3,5").
 * Errors reject with the POST's shape, `error.data.error.{code,message}`;
 * an abort rejects with an `AbortError` (no message to show).
 */
export async function importPortfolioDocument(
  file: File,
  pages?: string,
  options: ImportOptions = {},
): Promise<DocumentImportResult> {
  if (isPortfolioDocUploadMockEnabled()) return mockImport(file, pages, options);
  return importOverWebSocket(file, pages, options, await authPayload());
}

export const isAbortError = (error: unknown) => (error as { name?: string } | null)?.name === 'AbortError';

const abortError = () => Object.assign(new Error('Caricamento annullato'), { name: 'AbortError' });

const importError = (code: string, message?: string) =>
  Object.assign(new Error(message || code), { data: { error: { code, message } } });

// The browser cannot set Jwt-Jwks on a WebSocket and a token never goes in the URL:
// it travels in the first message, the same token RestService would send.
async function authPayload(): Promise<{ jwt: string } | { firebase: string } | null> {
  const jwt = JwtAuthService.shouldUseJwtAuth() ? JwtAuthService.getToken() : null;
  if (jwt) return { jwt };
  const firebase = await FirebaseTokenService.getCurrentToken().catch(() => null);
  return firebase ? { firebase } : null;
}

function socketUrl(): string {
  const config = window.__APP_CONFIG__;
  // Local dev: PORTFOLIO_DOC_UPLOAD_API_BASE_URL points at the upload api when it runs apart from the login api.
  const base = config?.PORTFOLIO_DOC_UPLOAD_API_BASE_URL || config?.BACKEND_API_BASE_URL || `https://${toApiHost()}`;
  return `${base.replace(/^http/, 'ws')}${IMPORT_DOCUMENT_WS_PATH}`;
}

function importOverWebSocket(
  file: File,
  pages: string | undefined,
  { onProgress, signal }: ImportOptions,
  auth: Awaited<ReturnType<typeof authPayload>>,
): Promise<DocumentImportResult> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const socket = new WebSocket(socketUrl());
    let settled = false;
    const settle = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      outcome();
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) socket.close();
    };
    const onAbort = () => settle(() => reject(abortError()));
    signal?.addEventListener('abort', onAbort, { once: true });

    const dot = file.name.lastIndexOf('.');
    socket.onopen = () =>
      socket.send(
        JSON.stringify({
          type: 'start',
          auth,
          extension: dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '',
          content_type: file.type || null,
          size: file.size,
          pages: pages ?? null,
        }),
      );
    socket.onmessage = (event: MessageEvent<string>) => {
      const message = JSON.parse(event.data);
      if (message.type === 'ready') {
        // Blob slices go out as binary frames; the browser reads them lazily.
        for (let start = 0; start < file.size; start += CHUNK_BYTES) socket.send(file.slice(start, start + CHUNK_BYTES));
      } else if (message.type === 'progress') {
        onProgress?.({
          stage: message.stage,
          message: message.message,
          done: message.done,
          total: message.total,
          elapsedS: message.elapsed_s ?? 0,
        });
      } else if (message.type === 'result') {
        const { type: _type, ...result } = message;
        settle(() => resolve(result as DocumentImportResult));
      } else if (message.type === 'error') {
        settle(() => reject(importError(message.code, message.message)));
      }
    };
    // A dropped connection (network, proxy, server restart) before any answer.
    socket.onclose = () => settle(() => reject(importError('internal')));
  });
}

// Mock: the file name prefix picks the case — error_* → the error fixture,
// needs_pages_* → needs_pages (ok once pages are chosen), anything else → ok.
// Progress plays the server's stages so the dialog can be tried without the api.
async function mockImport(file: File, pages: string | undefined, options: ImportOptions): Promise<DocumentImportResult> {
  const steps: Array<Omit<ImportProgress, 'elapsedS'>> = [
    { stage: 'reading', message: 'Lettura del documento…', done: null, total: null },
    { stage: 'isin', message: 'Strumenti trovati (ISIN): 5', done: 5, total: 5 },
    { stage: 'figures', message: 'Lettura degli importi: 3 di 5 righe…', done: 3, total: 5 },
    { stage: 'checks', message: 'Controllo dei totali…', done: null, total: null },
  ];
  for (let index = 0; index < steps.length; index += 1) {
    if (options.signal?.aborted) throw abortError();
    options.onProgress?.({ ...steps[index], elapsedS: index });
    await new Promise((resolve) => setTimeout(resolve, MOCK_STEP_MS));
  }
  if (options.signal?.aborted) throw abortError();
  const name = file.name.toLowerCase();
  if (name.startsWith('error_')) {
    throw Object.assign(new Error(errorFixture.body.error.message), {
      status: errorFixture.status,
      data: errorFixture.body,
    });
  }
  if (name.startsWith('needs_pages_') && !pages) return needsPagesFixture as DocumentImportResult;
  return okFixture as DocumentImportResult;
}
